/**
 * 20260929 - THE VIDEO FAN-OUT for the laptop pose's playback mirror (components/TimelinePlaybackMirror.tsx).
 *
 * WHAT IT IS. The Timeline tab's Viewer decodes a video shot once per painted role — the PRIMARY picture
 * and, during a dissolve, the TRANSITION partner — through useVideoSync (mp4box + WebCodecs). The mirror in
 * the top half must show the same frames, in the same tick, without a second decoder: iOS decode sessions
 * are finite and unmeasured on the foldable, and a second decoder would also mean a second copy of the MP4
 * bytes. So the one decoder that paints the Viewer's canvas also paints the mirror's, through this module.
 *
 * HOW. useVideoSync is told its role ONLY by the Viewer's two painted calls (never the next-pair pre-seek,
 * never FullscreenViewer, which is never on screen together with the split). It registers its own canvas
 * here as the role's SOURCE and, on every decoded frame, hands the frame to paintVideoMirror. The mirror
 * registers its canvas as the role's TARGET; registration blits the source once, so a paused frame shows
 * at once instead of waiting for the next seek. A decoder recreated for a new shot re-sizes the target
 * (sizeVideoMirror), which also clears it — the same blank the Viewer's own canvas shows until the first
 * frame of the new shot lands.
 *
 * A LEAF: no stores, no React, no imports of ours. Nothing is registered (and nothing is drawn) unless the
 * mirror is mounted, i.e. only in the laptop pose on the Timeline tab.
 */

export type VideoMirrorRole = 'primary' | 'transition';

interface MirrorTarget {
  canvas: HTMLCanvasElement;
  /** Told the intrinsic size whenever it changes (the mirror letterboxes on it when the project has no
   *  aspect ratio of its own, as the Viewer does with its decoder's dimensions). */
  onSize?: (width: number, height: number) => void;
}

const targets = new Map<VideoMirrorRole, MirrorTarget>();
const sources = new Map<VideoMirrorRole, { readonly current: HTMLCanvasElement | null }>();

function contextOf(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}

function resize(target: MirrorTarget, width: number, height: number): void {
  if (!(width > 0) || !(height > 0)) return;
  if (target.canvas.width === width && target.canvas.height === height) return;
  target.canvas.width = width;
  target.canvas.height = height;
  target.onSize?.(width, height);
}

/**
 * The Viewer's canvas for a role, held as its ref so the blit reads whatever element is mounted NOW.
 * Returns the unregister; a stale unregister (an older ref) leaves a newer source alone.
 */
export function setVideoMirrorSource(
  role: VideoMirrorRole,
  sourceRef: { readonly current: HTMLCanvasElement | null },
): () => void {
  sources.set(role, sourceRef);
  return () => {
    if (sources.get(role) === sourceRef) sources.delete(role);
  };
}

/**
 * The mirror's canvas for a role. Blits the role's source once (a paused frame shows immediately).
 * Returns the unregister; a stale unregister leaves a newer target alone.
 */
export function registerVideoMirrorTarget(
  role: VideoMirrorRole,
  canvas: HTMLCanvasElement,
  onSize?: (width: number, height: number) => void,
): () => void {
  const target: MirrorTarget = { canvas, onSize };
  targets.set(role, target);

  const source = sources.get(role)?.current ?? null;
  if (source !== null && source.width > 0 && source.height > 0) {
    resize(target, source.width, source.height);
    const ctx = contextOf(canvas);
    try {
      ctx?.drawImage(source, 0, 0, canvas.width, canvas.height);
    } catch {
      // A source canvas that cannot be read (never painted, or torn down mid-call) blits nothing; the next
      // decoded frame paints the mirror anyway.
    }
  }

  return () => {
    if (targets.get(role) === target) targets.delete(role);
  };
}

/** A decoder for this role was (re)configured at this size: size the mirror to match, clearing it. */
export function sizeVideoMirror(role: VideoMirrorRole, width: number, height: number): void {
  const target = targets.get(role);
  if (target !== undefined) resize(target, width, height);
}

/** One decoded frame for this role: drawn into the mirror's canvas when one is registered, else nothing. */
export function paintVideoMirror(role: VideoMirrorRole, frame: CanvasImageSource, width: number, height: number): void {
  const target = targets.get(role);
  if (target === undefined) return;
  resize(target, width, height);
  const ctx = contextOf(target.canvas);
  if (ctx === null) return;
  try {
    ctx.drawImage(frame, 0, 0, target.canvas.width, target.canvas.height);
  } catch {
    // Never let the mirror break the Viewer's own paint.
  }
}

/** Test seam only: forgets every registration. */
export function resetVideoMirrorForTests(): void {
  targets.clear();
  sources.clear();
}
